/** @jsxImportSource @emotion/react */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { css } from "@emotion/react";
import BaseInput from "components/common/Input/Base";
import { ExtendedOption } from "components/common/Select/Base";
import { PALETTE } from "styles/base/variables";

export interface BusinessAutocompleteProps {
  options: ExtendedOption[];
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onSelect: (businessId: string) => void;
}

// 텍스트를 입력하면 일치하는 사업자를 드롭다운으로 보여주는 검색형 선택 필드
function BusinessAutocomplete({
  options,
  value,
  disabled,
  placeholder = "사업자명 검색",
  onSelect,
}: BusinessAutocompleteProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const selectedLabel = options.find((opt) => opt.value === value)?.label || "";

  const [inputValue, setInputValue] = useState(selectedLabel);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  // 드롭다운을 document.body에 포털로 띄우기 위한 위치값. Dialog 내부처럼
  // overflow(스크롤) 컨테이너 안에 있으면 절대 위치로는 컨테이너 밖으로 드롭다운이
  // 삐져나갈 때 잘려 보이는 문제가 있어, 화면 기준 고정 위치로 별도 렌더링한다.
  const [menuRect, setMenuRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    setInputValue(selectedLabel);
  }, [selectedLabel]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
        setInputValue(selectedLabel);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedLabel]);

  const updateMenuRect = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMenuRect({ top: rect.bottom + 2, left: rect.left, width: rect.width });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuRect();

    // scroll 이벤트는 버블링되지 않으므로 capture 단계에서 window로 감지해
    // Dialog 내부 스크롤 컨테이너의 스크롤도 함께 잡아낸다.
    window.addEventListener("scroll", updateMenuRect, true);
    window.addEventListener("resize", updateMenuRect);
    return () => {
      window.removeEventListener("scroll", updateMenuRect, true);
      window.removeEventListener("resize", updateMenuRect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const filteredOptions = inputValue
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    : options;

  // 입력값이 바뀌어 필터링된 목록이 달라질 때마다 하이라이트를 맨 위로 되돌린다.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [inputValue, isOpen]);

  const moveHighlight = (delta: number) => {
    if (filteredOptions.length === 0) return;
    setHighlightedIndex((prev) => {
      const next =
        (prev + delta + filteredOptions.length) % filteredOptions.length;
      optionRefs.current[next]?.scrollIntoView({ block: "nearest" });
      return next;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      else moveHighlight(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      else moveHighlight(-1);
    } else if (e.key === "Enter") {
      if (!isOpen) return;
      e.preventDefault();
      const opt = filteredOptions[highlightedIndex];
      if (opt) {
        onSelect(opt.value.toString());
        setInputValue(opt.label);
        setIsOpen(false);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setInputValue(selectedLabel);
    }
  };

  return (
    <div
      ref={containerRef}
      css={css`
        position: relative;
        width: 240px;
      `}
    >
      <BaseInput
        placeholder={placeholder}
        value={inputValue}
        disabled={disabled}
        onChange={(e) => {
          setInputValue(e.target.value);
          setIsOpen(true);
        }}
        onFocus={(e) => {
          setIsOpen(true);
          e.currentTarget.select();
        }}
        onKeyDown={handleKeyDown}
        css={css`
          width: 100%;
          box-sizing: border-box;
        `}
      />
      {isOpen &&
        filteredOptions.length > 0 &&
        menuRect &&
        createPortal(
          <div
            ref={menuRef}
            css={css`
              position: fixed;
              top: ${menuRect.top}px;
              left: ${menuRect.left}px;
              width: ${menuRect.width}px;
              max-height: 220px;
              overflow-y: auto;
              background-color: ${PALETTE.light};
              border: 1px solid ${PALETTE.border};
              border-radius: 4px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
              z-index: 1500;
            `}
          >
            {filteredOptions.map((opt, idx) => (
              <div
                key={opt.value}
                ref={(el) => (optionRefs.current[idx] = el)}
                onMouseEnter={() => setHighlightedIndex(idx)}
                onMouseDown={(e) => {
                  // click 대신 mousedown에서 처리 + preventDefault로 input이 blur되지 않게 막는다.
                  // 그렇지 않으면 한글 입력 중(조합 미완료) 클릭 시 blur가 조합을 강제로
                  // 커밋시키면서 선택된 값 뒤에 입력하던 키워드가 그대로 붙는 문제가 있었다.
                  e.preventDefault();
                  onSelect(opt.value.toString());
                  setInputValue(opt.label);
                  setIsOpen(false);
                }}
                css={css`
                  padding: 8px 12px;
                  font-size: 13px;
                  cursor: pointer;
                  background-color: ${idx === highlightedIndex
                    ? PALETTE.primaryHover
                    : "transparent"};
                `}
              >
                {opt.label}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

export default BusinessAutocomplete;
